import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target,
  FlaskConical,
  Cog,
  Database,
  Rocket,
  RefreshCw,
  Stethoscope,
  Landmark,
  Factory,
  ShoppingCart,
  Car,
  Zap,
  Smartphone,
  Sprout,
  Brain,
  BarChart3,
  Eye,
  MessageSquare,
  TrendingUp,
  Gamepad2,
  Search,
  Sparkles,
  LineChart,
  Palette,
  Volume2,
  Layers,
  ArrowRight,
  Check,
  ChevronRight,
  CreditCard,
  FileText,
  Wheat,
  X,
  Loader,
  Code
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= PROJECT INQUIRY MODAL =============
const ProjectInquiryModal = ({ isOpen, onClose, selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    projectType: '',
    dataAvailability: '',
    timeline: '',
    budget: selectedPlan || '',
    description: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  // Update budget when selectedPlan changes
  React.useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({ ...prev, budget: selectedPlan }));
    }
  }, [selectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.description) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          interest: 'Custom AI/ML Solutions',
          message: `Custom AI/ML Project Inquiry\n\nCompany: ${formData.company}\nIndustry: ${formData.industry || 'Not specified'}\nProject Type: ${formData.projectType || 'Not specified'}\nData Availability: ${formData.dataAvailability || 'Not specified'}\nTimeline: ${formData.timeline || 'Not specified'}\nBudget: ${formData.budget || 'Not specified'}\n\nProject Description:\n${formData.description}`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Request submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', company: '', industry: '', projectType: '', dataAvailability: '', timeline: '', budget: '', description: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Project inquiry error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', company: '', industry: '', projectType: '', dataAvailability: '', timeline: '', budget: selectedPlan || '', description: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Project Request Received!</h3>
            <p className="text-gray-400 mb-6">
              Our engineering team will review your requirements and schedule a discovery call within 24-48 hours.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Code size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">Custom Development</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Start Your Project</h2>
            <p className="text-teal-400 mb-6">Let's build something amazing together</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Work Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your work email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Healthcare" className="bg-slate-950">Healthcare</option>
                    <option value="Finance" className="bg-slate-950">Finance</option>
                    <option value="Manufacturing" className="bg-slate-950">Manufacturing</option>
                    <option value="E-Commerce" className="bg-slate-950">E-Commerce</option>
                    <option value="Automotive" className="bg-slate-950">Automotive</option>
                    <option value="Energy" className="bg-slate-950">Energy</option>
                    <option value="Telecom" className="bg-slate-950">Telecom</option>
                    <option value="Agriculture" className="bg-slate-950">Agriculture</option>
                    <option value="Other" className="bg-slate-950">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Project Type</label>
                  <select
                    value={formData.projectType}
                    onChange={(e) => handleChange('projectType', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Computer Vision" className="bg-slate-950">Computer Vision</option>
                    <option value="NLP / LLM" className="bg-slate-950">NLP / LLM</option>
                    <option value="Predictive Analytics" className="bg-slate-950">Predictive Analytics</option>
                    <option value="Recommendation System" className="bg-slate-950">Recommendation</option>
                    <option value="Time Series" className="bg-slate-950">Time Series</option>
                    <option value="Anomaly Detection" className="bg-slate-950">Anomaly Detection</option>
                    <option value="Generative AI" className="bg-slate-950">Generative AI</option>
                    <option value="Multiple / Not Sure" className="bg-slate-950">Multiple / Not Sure</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Data Available?</label>
                  <select
                    value={formData.dataAvailability}
                    onChange={(e) => handleChange('dataAvailability', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="Yes, ready to use" className="bg-slate-950">Yes, ready</option>
                    <option value="Partial data available" className="bg-slate-950">Partial</option>
                    <option value="Need to collect" className="bg-slate-950">Need to collect</option>
                    <option value="Not sure" className="bg-slate-950">Not sure</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Timeline</label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                  >
                    <option value="" className="bg-slate-950">Select</option>
                    <option value="ASAP" className="bg-slate-950">ASAP</option>
                    <option value="1-3 months" className="bg-slate-950">1-3 months</option>
                    <option value="3-6 months" className="bg-slate-950">3-6 months</option>
                    <option value="6+ months" className="bg-slate-950">6+ months</option>
                    <option value="Flexible" className="bg-slate-950">Flexible</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Budget Range</label>
                <select
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select budget</option>
                  <option value="Proof of Concept ($25K)" className="bg-slate-950">Proof of Concept ($25K)</option>
                  <option value="Custom Solution ($75K)" className="bg-slate-950">Custom Solution ($75K)</option>
                  <option value="Enterprise Platform ($250K+)" className="bg-slate-950">Enterprise Platform ($250K+)</option>
                  <option value="Custom / Not Sure" className="bg-slate-950">Custom / Not Sure</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Project Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition resize-none"
                  placeholder="Describe your project, the problem you're solving, and expected outcomes..."
                />
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Rocket size={18} />
                    Start Project
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onStartProject }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-white transition">Home</Link>
            <Link to="/services" className="text-sm text-gray-300 hover:text-white transition">Services</Link>
            <a href="#industries" className="text-sm text-gray-300 hover:text-white transition">Industries</a>
            <a href="#pricing" className="text-sm text-gray-300 hover:text-white transition">Pricing</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Start Project
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onStartProject }) => {
  const stats = [
    { value: '300+', label: 'Custom Models Built' },
    { value: '25+', label: 'Industries Served' },
    { value: '98%', label: 'Accuracy Average' },
    { value: '6-16', label: 'Weeks to Deploy' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      <GradientOrbs />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.2),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badges */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {['Bespoke Development', 'Tailored Solutions', 'Your Vision, Our Code'].map((badge, i) => (
            <span
              key={i}
              className="text-xs font-semibold text-slate-900 px-4 py-2 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400"
            >
              {badge}
            </span>
          ))}
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="text-white">Custom AI &</span>
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            ML Solutions
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mb-4 font-light tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Precision-Engineered AI Systems Built for Your Unique Challenges
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-base md:text-lg text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Off-the-shelf solutions don't fit your unique business? We architect and build custom 
          AI/ML systems from the ground up—tailored to your specific requirements, data, workflows, 
          and objectives. From concept to deployment, we engineer intelligent systems that solve 
          your exact problems.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-2"
          >
            Start Your Project <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-400/40"
          >
            View Portfolio
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative group"
              whileHover={{ y: -5 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= SERVICE CARD =============
const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-100px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:border-teal-400/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

        <div className="relative z-10 flex flex-col h-full">
          <motion.div
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-6 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all"
            animate={isHovered ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-teal-300" size={32} />
          </motion.div>

          <h3 className="text-xl font-bold text-white mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all">
            {service.title}
          </h3>

          <p className="text-gray-400 mb-6 leading-relaxed flex-grow">
            {service.description}
          </p>

          <ul className="space-y-3">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-400 flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
              >
                <ChevronRight size={14} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span>{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= SERVICES SECTION =============
const ServicesSection = () => {
  const services = [
    {
      icon: Target,
      title: 'Problem Analysis & Design',
      description: 'Deep dive into your specific challenge. We analyze requirements, explore feasibility, evaluate approaches, and architect the optimal custom solution tailored to your context.',
      features: [
        'Comprehensive requirements gathering',
        'Feasibility studies & technical validation',
        'Custom architecture design',
        'Algorithm selection & justification',
        'Data requirements specification'
      ]
    },
    {
      icon: FlaskConical,
      title: 'Research & Prototyping',
      description: 'Rapid experimentation with cutting-edge techniques. We build proof-of-concepts, test novel approaches, and validate solutions before full-scale development.',
      features: [
        'Algorithm research & experimentation',
        'Rapid prototyping & iteration',
        'Benchmark testing & validation',
        'Performance optimization studies',
        'Technical risk assessment'
      ]
    },
    {
      icon: Cog,
      title: 'Model Development',
      description: 'Build production-grade custom models from scratch. We engineer architectures, train models, fine-tune performance, and ensure reliability for your specific use case.',
      features: [
        'Custom neural network architectures',
        'Transfer learning & fine-tuning',
        'Ensemble model development',
        'Hyperparameter optimization',
        'Cross-validation & testing'
      ]
    },
    {
      icon: Database,
      title: 'Data Engineering',
      description: 'Transform raw data into ML-ready assets. We build custom data pipelines, engineer features, handle edge cases, and create robust data infrastructure.',
      features: [
        'Custom ETL pipeline development',
        'Feature engineering & selection',
        'Data augmentation strategies',
        'Handling imbalanced datasets',
        'Data quality frameworks'
      ]
    },
    {
      icon: Rocket,
      title: 'Deployment & Integration',
      description: 'Seamlessly integrate custom models into your existing systems. We build APIs, optimize for production, ensure scalability, and integrate with your tech stack.',
      features: [
        'Custom API development',
        'Model optimization & compression',
        'Batch & real-time inference',
        'System integration & orchestration',
        'Performance monitoring setup'
      ]
    },
    {
      icon: RefreshCw,
      title: 'Continuous Improvement',
      description: 'Keep your custom models performing optimally. We monitor performance, retrain with new data, adapt to changes, and continuously enhance capabilities.',
      features: [
        'Performance monitoring & alerting',
        'Automated retraining pipelines',
        'A/B testing & experimentation',
        'Drift detection & mitigation',
        'Continuous model improvement'
      ]
    }
  ];

  return (
    <section id="services" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Core Capabilities
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Custom AI/ML Services
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            End-to-end custom AI/ML development services covering the full spectrum from research 
            and prototyping to production deployment and ongoing optimization.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= INDUSTRY SOLUTIONS SECTION =============
const IndustriesSection = () => {
  const industries = [
    {
      icon: Stethoscope,
      title: 'Healthcare & Life Sciences',
      description: 'Custom medical imaging, diagnostics, drug discovery, patient monitoring, and clinical decision support systems.',
      solutions: ['Medical image analysis & diagnostics', 'Genomics & drug discovery AI', 'Clinical prediction models', 'Patient risk stratification']
    },
    {
      icon: Landmark,
      title: 'Financial Services',
      description: 'Custom fraud detection, algorithmic trading, credit scoring, risk modeling, and financial forecasting systems.',
      solutions: ['Real-time fraud detection engines', 'Credit risk assessment models', 'Algorithmic trading strategies', 'Portfolio optimization systems']
    },
    {
      icon: Factory,
      title: 'Manufacturing & IoT',
      description: 'Custom predictive maintenance, quality control, process optimization, and supply chain intelligence systems.',
      solutions: ['Predictive maintenance models', 'Computer vision quality control', 'Production optimization AI', 'Supply chain forecasting']
    },
    {
      icon: ShoppingCart,
      title: 'E-Commerce & Retail',
      description: 'Custom recommendation engines, demand forecasting, dynamic pricing, visual search, and customer analytics.',
      solutions: ['Personalized recommendation systems', 'Demand forecasting models', 'Visual search & similarity', 'Customer lifetime value prediction']
    },
    {
      icon: Car,
      title: 'Automotive & Mobility',
      description: 'Custom autonomous systems, driver assistance, predictive diagnostics, route optimization, and fleet management AI.',
      solutions: ['Perception & sensor fusion', 'Driver behavior analysis', 'Predictive vehicle diagnostics', 'Route optimization engines']
    },
    {
      icon: Zap,
      title: 'Energy & Utilities',
      description: 'Custom demand forecasting, grid optimization, renewable energy prediction, and infrastructure monitoring systems.',
      solutions: ['Energy demand forecasting', 'Renewable energy prediction', 'Grid anomaly detection', 'Asset health monitoring']
    },
    {
      icon: Smartphone,
      title: 'Telecommunications',
      description: 'Custom network optimization, churn prediction, customer analytics, and fraud detection for telecom operations.',
      solutions: ['Network traffic prediction', 'Customer churn modeling', 'Anomaly & fraud detection', 'Service quality optimization']
    },
    {
      icon: Wheat,
      title: 'Agriculture & AgTech',
      description: 'Custom crop monitoring, yield prediction, disease detection, and precision agriculture intelligence systems.',
      solutions: ['Crop health monitoring via imagery', 'Yield prediction models', 'Pest & disease detection', 'Precision farming optimization']
    }
  ];

  return (
    <section id="industries" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Industry Solutions
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Vertical Expertise
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Deep domain knowledge across industries. We understand your sector's unique challenges 
            and build custom AI solutions that address industry-specific requirements.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-4 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <industry.icon className="text-teal-300" size={28} />
                </div>

                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                  {industry.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {industry.description}
                </p>

                <ul className="space-y-2">
                  {industry.solutions.map((solution, i) => (
                    <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                      <ArrowRight size={12} className="text-teal-400 mt-0.5 flex-shrink-0" />
                      <span>{solution}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= TECHNICAL CAPABILITIES SECTION =============
const TechCapabilitiesSection = () => {
  const capabilities = [
    { icon: Brain, title: 'Deep Learning', description: 'CNNs, RNNs, Transformers, GANs, autoencoders, attention mechanisms, and custom architectures.' },
    { icon: BarChart3, title: 'Classical ML', description: 'Gradient boosting, random forests, SVMs, ensemble methods, and traditional algorithms.' },
    { icon: Eye, title: 'Computer Vision', description: 'Object detection, segmentation, tracking, 3D reconstruction, and facial recognition.' },
    { icon: MessageSquare, title: 'NLP & LLMs', description: 'Text classification, NER, sentiment analysis, language generation, and fine-tuned LLMs.' },
    { icon: TrendingUp, title: 'Time Series', description: 'Forecasting, anomaly detection, sequence modeling, ARIMA, LSTM, and temporal fusion.' },
    { icon: Gamepad2, title: 'Reinforcement Learning', description: 'Policy optimization, Q-learning, actor-critic methods, and multi-agent systems.' },
    { icon: Search, title: 'Anomaly Detection', description: 'Unsupervised outlier detection, isolation forests, and autoencoders.' },
    { icon: Sparkles, title: 'Recommendation Systems', description: 'Collaborative filtering, content-based, hybrid approaches, and deep learning recommenders.' },
    { icon: LineChart, title: 'Optimization', description: 'Genetic algorithms, simulated annealing, and gradient-free optimization.' },
    { icon: Palette, title: 'Generative AI', description: 'Diffusion models, GANs, VAEs, text-to-image, and custom generative systems.' },
    { icon: Volume2, title: 'Speech & Audio', description: 'Speech recognition, synthesis, speaker identification, and audio classification.' },
    { icon: Layers, title: 'Transfer Learning', description: 'Fine-tuning pre-trained models, domain adaptation, and few-shot learning.' }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Technical Capabilities
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AI/ML Technologies
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Comprehensive expertise across the AI/ML technology landscape—from classical machine 
            learning to cutting-edge deep learning and beyond.
          </p>
        </motion.div>

        <motion.div
          className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                className="group p-5 bg-white/[0.03] border border-white/10 rounded-xl hover:border-teal-500/30 transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ y: -3 }}
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-emerald-500/30 flex items-center justify-center mb-3 group-hover:from-blue-500/50 group-hover:to-emerald-500/50 transition-all">
                  <cap.icon className="text-teal-300" size={20} />
                </div>
                <h3 className="text-sm font-bold text-teal-400 mb-2">
                  {cap.title}
                </h3>
                <p className="text-gray-400 text-xs leading-relaxed">
                  {cap.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= PROCESS SECTION =============
const ProcessSection = () => {
  const steps = [
    { number: 1, title: 'Discovery & Scoping', description: 'In-depth consultation to understand your problem, objectives, constraints, data, and success criteria.' },
    { number: 2, title: 'Data Assessment', description: 'Comprehensive analysis of available data—quality, quantity, distribution, and gaps.' },
    { number: 3, title: 'Architecture Design', description: 'Design custom system architecture tailored to your requirements. Algorithm selection and infrastructure planning.' },
    { number: 4, title: 'Rapid Prototyping', description: 'Build proof-of-concept to validate approach and demonstrate feasibility before full development.' },
    { number: 5, title: 'Model Development', description: 'Engineer production-grade custom models with rigorous training, validation, and testing.' },
    { number: 6, title: 'Integration & Deployment', description: 'Deploy models into your production environment with APIs, monitoring, and scaling.' },
    { number: 7, title: 'Monitoring & Optimization', description: 'Continuous performance monitoring, retraining, and improvement over time.' },
    { number: 8, title: 'Knowledge Transfer', description: 'Comprehensive training and documentation for your team to maintain the system.' }
  ];

  return (
    <section className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Development Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How We Build Custom AI/ML
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Our proven methodology for delivering custom AI/ML solutions—from initial concept 
            through production deployment and ongoing optimization.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300 h-full"
                whileHover={{ y: -5 }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-2xl" />

                <motion.div
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center mb-4 text-white font-bold text-lg"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {step.number}
                </motion.div>

                <h3 className="text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text mb-3">
                  {step.title}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PRICING SECTION =============
const PricingSection = ({ onSelectPlan }) => {
  const plans = [
    {
      tier: 'Proof of Concept',
      price: '$25K',
      period: '4-6 Week Sprint',
      featured: false,
      planValue: 'Proof of Concept ($25K)',
      features: [
        'Problem validation & scoping',
        'Data feasibility analysis',
        'Prototype model development',
        'Performance benchmarking',
        'Technical recommendations',
        'Architecture proposal',
        'Go/no-go decision support'
      ]
    },
    {
      tier: 'Custom Solution',
      price: '$75K',
      period: '10-16 Week Project',
      featured: true,
      planValue: 'Custom Solution ($75K)',
      features: [
        'Full custom model development',
        'Data pipeline engineering',
        'Production-ready deployment',
        'API & integration layer',
        'Monitoring & alerting setup',
        'Comprehensive documentation',
        'Team training & handoff',
        '3 months post-launch support'
      ]
    },
    {
      tier: 'Enterprise Platform',
      price: '$250K+',
      period: '6+ Month Engagement',
      featured: false,
      planValue: 'Enterprise Platform ($250K+)',
      features: [
        'Multi-model AI platform',
        'Custom MLOps infrastructure',
        'Advanced feature engineering',
        'Scalable architecture design',
        'Continuous learning systems',
        'Executive workshops & training',
        '12 months optimization support',
        'Dedicated engineering team'
      ]
    }
  ];

  return (
    <section id="pricing" className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Investment
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Custom Pricing
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Flexible engagement models for custom AI/ML development. Every project is unique—pricing 
            reflects scope, complexity, and timeline requirements.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`relative group ${plan.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full text-xs font-bold text-slate-900">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${
                plan.featured 
                  ? 'border-teal-500/50 shadow-lg shadow-teal-500/20' 
                  : 'border-white/10 hover:border-teal-500/30'
              }`}>
                <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-3xl ${
                  plan.featured 
                    ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500' 
                    : 'bg-gradient-to-r from-blue-500/50 via-teal-500/50 to-emerald-500/50'
                }`} />

                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">
                  {plan.tier}
                </h3>

                <div className="text-center mb-6">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {plan.period}
                  </div>
                </div>

                <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400">
                      <Check size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onSelectPlan(plan.planValue)}
                  className={`w-full py-4 rounded-full font-semibold transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-teal-500/50'
                      : 'border border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50'
                  }`}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= PORTFOLIO SECTION =============
const PortfolioSection = () => {
  const projects = [
    {
      icon: Stethoscope,
      title: 'Radiology AI Assistant',
      description: 'Custom deep learning model for automated detection of 12 pathology types in chest X-rays.',
      metrics: [
        { value: '96.8%', label: 'Accuracy' },
        { value: '-65%', label: 'Review Time' }
      ],
      tags: ['Computer Vision', 'Healthcare', 'ResNet']
    },
    {
      icon: CreditCard,
      title: 'Real-Time Fraud Engine',
      description: 'Custom ensemble model processing 50K+ transactions per second with sub-100ms latency.',
      metrics: [
        { value: '99.2%', label: 'Fraud Catch Rate' },
        { value: '0.3%', label: 'False Positive' }
      ],
      tags: ['XGBoost', 'Fintech', 'Real-Time']
    },
    {
      icon: Factory,
      title: 'Predictive Maintenance System',
      description: 'Custom LSTM model analyzing sensor data from 200+ machines to predict failures 2-3 weeks ahead.',
      metrics: [
        { value: '87%', label: 'Downtime Reduction' },
        { value: '$3.2M', label: 'Annual Savings' }
      ],
      tags: ['Time Series', 'IoT', 'LSTM']
    },
    {
      icon: ShoppingCart,
      title: 'Personalization Engine',
      description: 'Custom deep learning recommender system processing user behavior across 5M+ products.',
      metrics: [
        { value: '+42%', label: 'Click-Through' },
        { value: '+28%', label: 'Conversion Rate' }
      ],
      tags: ['Recommender', 'E-Commerce', 'Neural CF']
    },
    {
      icon: FileText,
      title: 'Document Intelligence Platform',
      description: 'Custom NLP pipeline extracting structured data from unstructured legal documents.',
      metrics: [
        { value: '94.5%', label: 'Extraction Accuracy' },
        { value: '-92%', label: 'Processing Time' }
      ],
      tags: ['NLP', 'Legal Tech', 'BERT']
    },
    {
      icon: Wheat,
      title: 'Crop Disease Detection',
      description: 'Custom computer vision system analyzing drone and satellite imagery to detect crop diseases.',
      metrics: [
        { value: '93%', label: 'Detection Accuracy' },
        { value: '+31%', label: 'Yield Increase' }
      ],
      tags: ['Computer Vision', 'Agriculture', 'EfficientNet']
    }
  ];

  return (
    <section id="portfolio" className="relative py-24 px-4 overflow-hidden bg-slate-950/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="text-xs font-semibold text-teal-300 px-4 py-2 rounded-full border border-teal-500/50 bg-teal-500/10 inline-block mb-4">
            Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Custom Projects Delivered
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Real custom AI/ML solutions we've built—showcasing the diversity of problems solved 
            and measurable impact delivered across industries.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden hover:border-teal-500/30 transition-all duration-300 h-full flex flex-col">
                <div className="bg-gradient-to-r from-blue-500/20 via-teal-500/20 to-emerald-500/20 p-8 flex items-center justify-center relative overflow-hidden">
                  <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <project.icon className="text-teal-300" size={40} />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-bold text-teal-400 mb-3">
                    {project.title}
                  </h3>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-grow">
                    {project.description}
                  </p>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {project.metrics.map((metric, i) => (
                      <div
                        key={i}
                        className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center"
                      >
                        <div className="text-xl font-bold text-emerald-400">
                          {metric.value}
                        </div>
                        <div className="text-xs text-gray-400">
                          {metric.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 rounded-full border border-teal-500/30 text-teal-400 bg-teal-500/10"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onStartProject }) => {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-teal-500/20 to-emerald-600/20 blur-3xl" />

        <motion.div
          className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-12 md:p-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-t-3xl" />

          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">
            Build Your Custom AI/ML Solution
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Ready to solve your unique challenge with custom AI/ML? Let's discuss your requirements 
            and design the perfect solution for your specific needs.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartProject}
            className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition flex items-center gap-3 mx-auto"
          >
            Start Project <Rocket size={20} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/10 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </Link>
            <p className="text-gray-400 text-sm mt-4 leading-relaxed">
              Custom AI/ML solutions engineered for your unique challenges.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Services</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-engineer-augmentation" className="hover:text-teal-400 transition">Staff Augmentation</Link></li>
              <li><Link to="/ai-project-execution" className="hover:text-teal-400 transition">Project Execution</Link></li>
              <li><Link to="/ai-consulting-services" className="hover:text-teal-400 transition">Consulting</Link></li>
              <li><Link to="/custom-ai-ml-solutions" className="hover:text-teal-400 transition">Custom Solutions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/#about" className="hover:text-teal-400 transition">About</Link></li>
              <li><Link to="/training" className="hover:text-teal-400 transition">Training Programs</Link></li>
              <li><Link to="/services" className="hover:text-teal-400 transition">All Services</Link></li>
              <li><Link to="/#contact" className="hover:text-teal-400 transition">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Cookie Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function CustomAIMLSolutionsPage() {
  const [projectModal, setProjectModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleStartProject = (plan = null) => {
    setSelectedPlan(plan);
    setProjectModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onStartProject={() => handleStartProject()} />
      <Hero onStartProject={() => handleStartProject()} />
      <ServicesSection />
      <IndustriesSection />
      <TechCapabilitiesSection />
      <ProcessSection />
      <PricingSection onSelectPlan={handleStartProject} />
      <PortfolioSection />
      <FinalCTA onStartProject={() => handleStartProject()} />
      <Footer />

      {/* Project Inquiry Modal */}
      <ProjectInquiryModal
        isOpen={projectModal}
        onClose={() => setProjectModal(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
}